/**
 * Session schemas — mirror chaos.sessions (per SESSION_RUNTIME.md).
 * One row per card; the Docker container is named after `id`.
 *
 * Phase 3 will expand with sleep/wake events, resource usage, and the
 * keep-awake reservation contract.
 */
import { z } from 'zod';

// Session lifecycle states (per SESSION_RUNTIME.md §2). The wire shape
// carries all six. The collapsing described here — `created` into `idle`,
// `dead` into `error` — was chaos-ui-components' palette, a package deleted
// 2026-08-02 that rendered nothing in this deployment. Do not treat it as a
// statement about what apps/chaos-ui shows; read that tree.
export const sessionStates = [
  'created',
  'idle',
  'active',
  'sleeping',
  'waking',
  'error',
  'dead',
] as const;
export const sessionStateSchema = z.enum(sessionStates);
export type SessionState = z.infer<typeof sessionStateSchema>;

/**
 * Minimal cross-boundary shape used by the CLI terminal dispatcher. `mode` is
 * intentionally unknown: a newer gateway may add a value before an older UI
 * understands it, and that older UI must fail closed instead of attaching the
 * new mode to a terminal by accident.
 */
export interface TerminalSessionCandidate {
  id: string;
  sessionNumber: number;
  mode?: unknown;
  state?: unknown;
  createdAt?: string | null;
}

/** Only CLI rows and exact NULL legacy rows own the terminal surface. */
export function isTerminalSessionMode(mode: unknown): mode is 'cli' | null {
  return mode === 'cli' || mode === null;
}

function terminalCreatedAtMs(value: string | null | undefined): number {
  if (typeof value !== 'string') return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function newestTerminalFirst<T extends TerminalSessionCandidate>(a: T, b: T): number {
  const byNumber = b.sessionNumber - a.sessionNumber;
  if (byNumber !== 0) return byNumber;
  const byCreated = terminalCreatedAtMs(b.createdAt) - terminalCreatedAtMs(a.createdAt);
  if (byCreated !== 0) return byCreated;
  if (a.id === b.id) return 0;
  return a.id < b.id ? 1 : -1;
}

/**
 * Deterministically select the newest live terminal row. Input order is never
 * authority: the total order is session number, created time, then UUID.
 * Unknown/absent future modes and genuinely dead rows are excluded.
 */
export function pickNewestTerminalSession<T extends TerminalSessionCandidate>(
  sessions: readonly T[],
): T | null {
  const eligible = sessions.filter(
    (session) =>
      typeof session.id === 'string' &&
      session.id.length > 0 &&
      Number.isInteger(session.sessionNumber) &&
      session.sessionNumber > 0 &&
      isTerminalSessionMode(session.mode) &&
      session.state !== 'dead',
  );
  return [...eligible].sort(newestTerminalFirst)[0] ?? null;
}

// Sleep replay policy when a card wakes back up.
export const replayStrategies = ['none', 'lastN', 'full'] as const;
export const replayStrategySchema = z.enum(replayStrategies);
export type ReplayStrategy = z.infer<typeof replayStrategySchema>;

// chaos.sessions row.
export const sessionRowSchema = z.object({
  id: z.string().uuid(),
  cardId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  state: sessionStateSchema,
  containerName: z.string().min(1).max(128).nullable(),
  // Active model preset for this session (changes on column-driven auto
  // switch — ADR-031).
  modelPresetId: z.string().uuid().nullable(),
  replayStrategy: replayStrategySchema,
  // When the session last became `active` — used by the keep-awake watcher.
  lastActiveAt: z.string().datetime().nullable(),
  // Sleeping deadline (UTC) — when the session will sleep absent activity.
  sleepDeadlineAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SessionRow = z.infer<typeof sessionRowSchema>;

// Lifecycle action verbs the chat tab issues to the gateway.
// SESSION_RUNTIME.md §3.2 maps the UI vocabulary (start / pause / resume /
// end) to the backend state machine (created → waking → active ⇄ sleeping
// → waking → dead).
export const sessionActions = ['start', 'pause', 'resume', 'end'] as const;
export const sessionActionSchema = z.enum(sessionActions);
export type SessionAction = z.infer<typeof sessionActionSchema>;

export const sessionLifecycleResponseSchema = z.object({
  sessionId: z.string().uuid(),
  cardId: z.string().uuid(),
  state: sessionStateSchema,
  note: z.string().optional(),
});
export type SessionLifecycleResponse = z.infer<typeof sessionLifecycleResponseSchema>;
